import { authApi } from 'api';

/**
 * Get club event types
 * @param {string} clubId - The club ID
 * @param {boolean} includeInactive - Whether to include inactive event types (default: false)
 * @returns {Promise} - The response from the API
 */
export const getClubEventTypes = async (clubId, includeInactive = false) => {
  try {
    const queryParam = includeInactive ? '?includeInactive=true' : '';
    const response = await authApi.get(`/Clubs/${clubId}/event-types${queryParam}`);
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Create a new event type
 *
 * @param {string} clubId - The club ID.
 * @param {Object} eventType - An object containing the details of the event type to be created.
 * @param {string} eventType.displayName - The display name of the event type.
 * @param {string} eventType.category - The category of the event type.
 * @param {string} eventType.classification
 * @param {int} eventType.distanceInMeters
 * @param {boolean} eventType.isMultiLaneFinish
 * @param {int} eventType.sortOrder
 * @returns {Promise} - A promise resolving with the API response.
 */
export const createClubEventType = async (clubId, eventType) => {
  try {
    const response = await authApi.post(
      `/Clubs/${clubId}/event-types`,
      eventType
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Get club event type by code
 *
 * @param {string} clubId - The club ID.
 * @param {int} eventCode - The code of the event type
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const getClubEventTypeByCode = async (clubId, eventCode) => {
  try {
    const response = await authApi.get(
      `/Clubs/${clubId}/event-types/${eventCode}`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Update club event type by code
 *
 * @param {string} clubId - The club ID.
 * @param {int} eventCode - The code of the event type
 * @param {Object} eventType - An object containing the details of the event type to be created.
 * @param {string} eventType.displayName - The display name of the event type.
 * @param {string} eventType.category - The category of the event type.
 * @param {string} eventType.classification
 * @param {int} eventType.distanceInMeters
 * @param {boolean} eventType.isMultiLaneFinish
 * @param {int} eventType.sortOrder
 * @returns {Promise} - A promise resolving with the API response.
 */
export const updateClubEventTypeByCode = async (
  clubId,
  eventCode,
  eventType
) => {
  try {
    const response = await authApi.put(
      `/Clubs/${clubId}/event-types/${eventCode}`,
      eventType
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Delete club event type by code
 *
 * @param {string} clubId - The club ID.
 * @param {int} eventCode - The code of the event type
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const deleteClubEventTypeByCode = async (clubId, eventCode) => {
  try {
    const response = await authApi.delete(
      `/Clubs/${clubId}/event-types/${eventCode}`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * initialize club with standard event types
 *
 * @param {string} clubId - The club ID.
 * @param {int} eventCode - The code of the event type
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const initClubWithStandardEventType = async (clubId) => {
  try {
    const response = await authApi.post(
      `/Clubs/${clubId}/event-types/initialize`
    );
    return response;
  } catch (err) {
    throw err;
  }
};
